'use client'

import {
  IconSearch,
  IconBook2,
  IconLanguage,
  IconKeyboard,
  IconArrowRight,
  IconWriting
} from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { convertAccentedPinyinToNumbered } from '@/lib/convert-accents-to-numbers'
import { useTranslations } from 'next-intl'
import type { ReactNode, RefObject, KeyboardEvent } from 'react'

interface SearchBarProps {
  value: string
  onChange: (val: string) => void
  onSubmit?: () => void
  englishMode: boolean
  onToggleMode: () => void
  isLoading?: boolean
  showZhuyinButton?: boolean
  onToggleZhuyinKeyboard?: () => void
  isZhuyinKeyboardOpen?: boolean
  inputRef?: RefObject<HTMLInputElement | null>
  radicalControl?: ReactNode
  onOpenHandwriting?: () => void
}

export function SearchBar({
  value,
  onChange,
  onSubmit,
  englishMode,
  onToggleMode,
  isLoading,
  showZhuyinButton,
  onToggleZhuyinKeyboard,
  isZhuyinKeyboardOpen,
  inputRef,
  radicalControl,
  onOpenHandwriting
}: SearchBarProps) {
  const t = useTranslations('search')

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSubmit) {
      e.preventDefault()
      onSubmit()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Only convert accented pinyin in Chinese mode
    if (englishMode) {
      return // Let default paste behavior happen
    }

    const pastedText = e.clipboardData.getData('text')

    // Check if pasted text contains accented pinyin characters
    // If it doesn't (e.g., just Hanzi or plain text), let default paste happen
    const hasAccentedPinyin = /[āáǎàēéěèīíǐìōóǒòūúǔùǖǘǚǜü]/.test(pastedText)

    if (!hasAccentedPinyin) {
      return // Let default paste behavior happen for non-accented text
    }

    const converted = convertAccentedPinyinToNumbered(pastedText)

    // If conversion occurred (text changed), prevent default and use converted text
    if (converted !== pastedText.trim().toLowerCase()) {
      e.preventDefault()
      const input = e.currentTarget
      const start = input.selectionStart || 0
      const end = input.selectionEnd || 0
      const newValue = value.slice(0, start) + converted + value.slice(end)
      onChange(newValue)
      // Set cursor position after the pasted content
      setTimeout(() => {
        input.setSelectionRange(
          start + converted.length,
          start + converted.length
        )
      }, 0)
    }
  }

  const hasValue = value.trim().length > 0

  return (
    <div className="sticky top-0 z-10 flex gap-2 p-4">
      <div className="relative flex-1">
        <IconSearch className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />

        {/* Handwriting button - inside input, after search icon */}
        {!englishMode && onOpenHandwriting && (
          <button
            type="button"
            onClick={onOpenHandwriting}
            className={cn(
              'absolute top-1/2 left-8 z-10 -translate-y-1/2',
              'flex h-6 w-6 items-center justify-center rounded',
              'border-border/50 bg-muted/30 border',
              'text-muted-foreground hover:text-foreground hover:bg-muted hover:border-border',
              'transition-all duration-150 hover:scale-105',
              'focus:ring-primary/50 focus:ring-2 focus:outline-none'
            )}
            aria-label={t('openHandwriting')}
          >
            <IconWriting className="h-3.5 w-3.5" />
          </button>
        )}

        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          ref={inputRef}
          placeholder={englishMode ? t('searchEnglish') : t('searchHanzi')}
          className={cn(
            'bg-background py-2.5 pr-10 font-medium md:text-sm',
            !englishMode && onOpenHandwriting ? 'pl-14' : 'pl-10',
            isLoading && 'animate-pulse'
          )}
        />
        {/* Search submit button - animated arrow */}
        <button
          type="button"
          onClick={onSubmit}
          disabled={!hasValue}
          className={cn(
            'absolute top-1/2 right-2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md transition-all duration-200',
            'text-foreground/70 hover:text-foreground hover:bg-muted',
            hasValue
              ? 'translate-x-0 opacity-100'
              : 'pointer-events-none translate-x-2 opacity-0'
          )}
          aria-label="Search"
        >
          <IconArrowRight className="h-4 w-4" />
        </button>
      </div>

      {showZhuyinButton && (
        <Button
          type="button"
          variant={isZhuyinKeyboardOpen ? 'default' : 'outline'}
          className="hidden rounded-lg px-3 py-2 md:block"
          onClick={() => onToggleZhuyinKeyboard?.()}
          aria-pressed={isZhuyinKeyboardOpen}
          aria-label={isZhuyinKeyboardOpen ? t('closeZhuyin') : t('openZhuyin')}
        >
          <IconKeyboard className="h-4 w-4" />
        </Button>
      )}

      {radicalControl}

      <Button
        type="button"
        variant={englishMode ? 'default' : 'secondary'}
        className="rounded-lg px-3 py-2"
        onClick={onToggleMode}
        aria-label={englishMode ? t('switchToChinese') : t('switchToEnglish')}
      >
        {englishMode ? (
          <IconBook2 className="h-4 w-4" />
        ) : (
          <IconLanguage className="h-4 w-4" />
        )}
      </Button>
    </div>
  )
}
